import { defineStore } from 'pinia';
import axios from '../../axios';

export const useMenuStore = defineStore('menu', {
  state: () => ({
    items: [],           // Filtered items for OrderNow page
    allMenuItems: [],    // All unfiltered menu items
    cart: [],
    loading: false,
    error: null,
    loadGen: 0,          // bumped whenever the filter changes, to stop a stale background walk
    nextPageUrl: '/menu/items/',
    searchQuery: '',
    selectedItem: {},
    pendingOpenItem: null,  // full dish stashed by the homepage card click; opened on the menu page
    categories: [],
    categoriesLoading: false,
    categoriesError: null,
    selectedCategory: "",
    best_sellings:[],
    new_dishes:[],
    popular:[],
    availableCoupons:[],
  }),
  getters: {
    isAllItemsLoaded: (state) => !state.nextPageUrl,
  },
  actions: {
    async loadItems() {
      if (this.loading || !this.nextPageUrl) return;

      this.loading = true;
      const url = this.nextPageUrl;
      // The DRF `next` link (used for pages 2+) already carries page/search/category in its
      // query string. Only attach params for the FIRST page (bare "/menu/items/"); attaching
      // them again to a next-URL duplicated them each page (?search=&search=&search=…).
      const params = { search: this.searchQuery };
      if (this.selectedCategory) params.category = this.selectedCategory;
      const config = url.includes('?') ? {} : { params };

      // Retry a failed page a couple of times: a transient blip must never silently drop
      // dishes from the menu (leaving `nextPageUrl` intact lets the walk resume either way).
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(url, config);
          const data = response.data;
          this.items.push(...data.results);
          // Only update allMenuItems when unfiltered — preserves the full menu for
          // best_sellings and popular.
          if (!this.selectedCategory && !this.searchQuery) {
            this.allMenuItems.push(...data.results);
            this.filterItems();
          }
          this.nextPageUrl = data.next;
          this.error = null;
          this.loading = false;
          return;
        } catch (error) {
          lastErr = error;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => { setTimeout(resolve, 400 * (attempt + 1)); });
        }
      }
      this.error = lastErr;
      this.loading = false;
    },
    async loadAllRemaining() {
      // Load EVERY remaining page so browse + search always show the COMPLETE menu — a dish on
      // a later page must never be "missing" until the user happens to scroll to it. Guarded by
      // loadGen so a background walk stops the moment the filter (search / category) changes.
      const gen = this.loadGen;
      let guard = 0;
      while (this.nextPageUrl && gen === this.loadGen && guard < 40) {
        guard += 1;
        // eslint-disable-next-line no-await-in-loop
        await this.loadItems();
        if (this.error) break;   // loadItems already retried; stop but keep the position
      }
    },
    // Fetch the COMPLETE popular + best-selling + new sets (server-filtered, all pages) so
    // the homepage isn't limited to whatever happened to be on page 1 of the menu.
    async loadFeatured() {
      const fetchAll = async (param) => {
        const out = [];
        let url = '/menu/items/';
        let guard = 0;
        let params = { [param]: true };
        while (url && guard < 12) {
          guard += 1;
          // eslint-disable-next-line no-await-in-loop
          const { data } = await axios.get(url, { params });
          out.push(...(data.results || []));
          if (data.next) {
            const u = new URL(data.next);
            url = u.pathname.replace(/^\/api/, '') + u.search;
            params = {}; // the next URL already carries the filter + page
          } else {
            url = null;
          }
        }
        return out;
      };
      try {
        const [pop, best, fresh] = await Promise.all([fetchAll('is_popular'), fetchAll('is_best_selling'), fetchAll('is_new')]);
        if (pop.length) this.popular = pop;
        if (best.length) this.best_sellings = best;
        this.new_dishes = fresh;
      } catch (e) {
        this.error = e;
      }
    },
    async searchItems(query) {
      this.searchQuery = query;
      this.items = [];
      this.nextPageUrl = '/menu/items/';
      this.loadGen += 1;
      await this.loadItems();
      this.loadAllRemaining();   // load ALL matches, not just page 1 (e.g. 21 chicken dishes)
    },
    async loadCategories() {
      if (this.categoriesLoading) return;

      this.categoriesLoading = true;
      try {
        // The category endpoint is paginated (~10/page), so walk every page —
        // otherwise categories beyond the first page silently disappear.
        let url = '/menu/category/';
        let all = [];
        while (url) {
          const response = await axios.get(url);
          const data = response.data;
          all = all.concat(data.results || []);
          // DRF `next` is an absolute URL; make it relative so it stays on the axios baseURL.
          url = data.next ? data.next.replace(/^https?:\/\/[^/]+\/api/, '') : null;
        }
        this.categories = all;
      } catch (error) {
        this.categoriesError = error;
      } finally {
        this.categoriesLoading = false;
      }
    },
    async fetchCoupons(){
      // coupons are per-account; signed-out visitors have none (and would only get a 401)
      try { if (!localStorage.getItem('accessToken')) { this.availableCoupons = []; return; } } catch (e) { /* noop */ }
      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get('/transactions/coupons/orders/');
        this.availableCoupons = response.data.results;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to fetch coupons.';
        console.error('Error fetching coupons:', err);
      } finally {
        this.loading = false;
      }
    },
    selectCategory(category) {
      this.selectedCategory = category;
      this.items = [];
      this.nextPageUrl = '/menu/items/';
      this.loadGen += 1;
      this.loadItems().then(() => this.loadAllRemaining());
    },
    updateSelectedItem(item) {
      this.selectedItem = item;
    },
    filterItems(){
      // Filter from allMenuItems, not from items
      // This ensures best_sellings and popular always have all items
      this.best_sellings = this.allMenuItems.filter((item)=>item.is_best_selling === true);
      this.popular = this.allMenuItems.filter((item)=>item.is_popular === true);
      if (!this.new_dishes.length) this.new_dishes = this.allMenuItems.filter((item)=>item.is_new === true);
    },
    resetItems() {
      this.items = [];
      this.allMenuItems = [];  // Also reset allMenuItems
      this.nextPageUrl = '/menu/items/';
      this.loadGen += 1;       // cancel any in-flight background walk
    },
    findItem(meal_name) {
      // Search in allMenuItems for better results
      return this.allMenuItems.filter((item)=> item.name === meal_name)
    }
  },
});
